import { useState, useEffect, useCallback } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  BarChart3, GitBranch, Sparkles, FileText, FlaskConical,
  TrendingUp, Brain, Microscope, Beaker,
} from "lucide-react";

import StagedRcaTab from "./analyze/StagedRcaTab";
import AiRootCauseTab from "./analyze/AiRootCauseTab";
import ParetoTab from "./analyze/ParetoTab";
import FishboneTab, { DEFAULT_CATEGORIES } from "./analyze/FishboneTab";
import ManualTestsTab from "./analyze/ManualTestsTab";
import CapabilityTab from "./analyze/CapabilityTab";
import ExecutiveSummaryTab from "./analyze/ExecutiveSummaryTab";
import DoeTab from "./analyze/DoeTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AnalyzePhase({ projectId, token }) {
  const [datasets, setDatasets] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [loading, setLoading] = useState(true);

  // Cross-tab shared state
  const [rcaResult, setRcaResult] = useState(null);
  const [decisionReadiness, setDecisionReadiness] = useState(null);

  // Fishbone initial data (loaded once)
  const [initialFishbone, setInitialFishbone] = useState(null);
  const [initialExistingFishbone, setInitialExistingFishbone] = useState(null);

  // AI artifact persistence
  const [initialAiResult, setInitialAiResult] = useState(null);
  const [initialExecSummary, setInitialExecSummary] = useState(null);

  // Manual Tests tab state (kept in parent since handler needs shared dataset)
  const [testType, setTestType] = useState("t_test");
  const [columnA, setColumnA] = useState("");
  const [columnB, setColumnB] = useState("");
  const [alpha, setAlpha] = useState(0.05);
  const [testResult, setTestResult] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [datasetsRes, fishboneRes, drRes, aiArtifactsRes] = await Promise.all([
        fetch(`${API}/projects/${projectId}/datasets`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/projects/${projectId}/fishbone`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/projects/${projectId}/decision-readiness`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/ai/projects/${projectId}/ai-artifacts`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      if (datasetsRes.ok) {
        const data = await datasetsRes.json();
        setDatasets(data);
        if (data.length > 0) setSelectedDataset(data[0]);
      }

      if (fishboneRes.ok) {
        const fbArr = await fishboneRes.json();
        if (Array.isArray(fbArr) && fbArr.length > 0) {
          const fbData = fbArr[fbArr.length - 1];
          setInitialExistingFishbone(fbData);
          setInitialFishbone({
            effect: fbData.effect || "",
            categories: (fbData.categories || DEFAULT_CATEGORIES).map((c) => ({
              ...c,
              causes: c.causes?.length ? c.causes : [""],
            })),
          });
        }
      }

      if (drRes.ok) setDecisionReadiness(await drRes.json());

      if (aiArtifactsRes.ok) {
        const artifacts = await aiArtifactsRes.json();
        for (const art of artifacts) {
          if (art.artifact_type === "executive_summary" && art.data) setInitialExecSummary(art.data);
          if (art.artifact_type === "ai_root_cause" && art.data) setInitialAiResult(art.data);
        }
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  }, [projectId, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getCategoricalColumns = useCallback(() => {
    if (!selectedDataset?.profile?.columns) return [];
    return Object.entries(selectedDataset.profile.columns)
      .filter(([_, info]) => info.dtype === "object" || info.unique_count < 50)
      .map(([name]) => name);
  }, [selectedDataset]);

  const getNumericColumns = useCallback(() => {
    if (!selectedDataset?.profile?.columns) return [];
    return Object.entries(selectedDataset.profile.columns)
      .filter(([_, info]) => info.dtype.includes("int") || info.dtype.includes("float"))
      .map(([name]) => name);
  }, [selectedDataset]);

  const runHypothesisTest = useCallback(async () => {
    if (!selectedDataset || !columnA) {
      return;
    }
    if ((testType === "chi_square" || testType === "correlation" || testType === "anova") && !columnB) {
      return;
    }
    setAnalyzing(true);
    try {
      const response = await fetch(
        `${API}/projects/${projectId}/datasets/${selectedDataset.dataset_id}/hypothesis-test`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ test_type: testType, dataset_id: selectedDataset.dataset_id, column_a: columnA, column_b: columnB || null, alpha, alternative: "two-sided" }),
        }
      );
      if (!response.ok) {
        let errorMsg = "Test failed";
        try { const error = await response.json(); errorMsg = error.detail || errorMsg; } catch { /* non-JSON error */ }
        throw new Error(errorMsg);
      }
      const data = await response.json();
      setTestResult(data);
    } catch (error) {
      const { toast } = await import("sonner");
      toast.error(error.message);
    } finally {
      setAnalyzing(false);
    }
  }, [projectId, token, selectedDataset, testType, columnA, columnB, alpha]);

  if (loading) {
    return <div className="text-muted-foreground">Loading Analyze phase...</div>;
  }

  return (
    <div className="space-y-6" data-testid="analyze-phase">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold">Analyze Phase</h1>
          <p className="text-muted-foreground">Identify root causes and validate hypotheses</p>
        </div>
      </div>

      <Tabs defaultValue="staged-rca" className="space-y-6">
        <TabsList className="bg-card border border-border flex-wrap">
          <TabsTrigger value="staged-rca" className="gap-2" data-testid="tab-staged-rca">
            <Microscope className="w-4 h-4" />
            Staged RCA
          </TabsTrigger>
          <TabsTrigger value="ai-analysis" className="gap-2" data-testid="tab-ai-analysis">
            <Brain className="w-4 h-4" />
            AI Root Cause
          </TabsTrigger>
          <TabsTrigger value="pareto" className="gap-2" data-testid="tab-pareto">
            <BarChart3 className="w-4 h-4" />
            Pareto
          </TabsTrigger>
          <TabsTrigger value="fishbone" className="gap-2" data-testid="tab-fishbone">
            <GitBranch className="w-4 h-4" />
            Fishbone
          </TabsTrigger>
          <TabsTrigger value="hypothesis" className="gap-2" data-testid="tab-hypothesis">
            <FlaskConical className="w-4 h-4" />
            Manual Tests
          </TabsTrigger>
          <TabsTrigger value="capability" className="gap-2" data-testid="tab-capability">
            <TrendingUp className="w-4 h-4" />
            Capability
          </TabsTrigger>
          <TabsTrigger value="doe" className="gap-2" data-testid="tab-doe">
            <Beaker className="w-4 h-4" />
            DOE
          </TabsTrigger>
          <TabsTrigger value="summary" className="gap-2" data-testid="tab-summary">
            <FileText className="w-4 h-4" />
            Summary
          </TabsTrigger>
        </TabsList>

        <TabsContent value="staged-rca" className="space-y-6">
          <StagedRcaTab
            projectId={projectId}
            token={token}
            datasets={datasets}
            selectedDataset={selectedDataset}
            setSelectedDataset={setSelectedDataset}
            onRcaResult={setRcaResult}
          />
        </TabsContent>

        <TabsContent value="ai-analysis" className="space-y-6">
          <AiRootCauseTab
            projectId={projectId}
            token={token}
            datasets={datasets}
            selectedDataset={selectedDataset}
            setSelectedDataset={setSelectedDataset}
            rcaResult={rcaResult}
            decisionReadiness={decisionReadiness}
            getNumericColumns={getNumericColumns}
            initialAiResult={initialAiResult}
          />
        </TabsContent>

        <TabsContent value="pareto" className="space-y-6">
          <ParetoTab
            projectId={projectId}
            token={token}
            datasets={datasets}
            selectedDataset={selectedDataset}
            setSelectedDataset={setSelectedDataset}
            getCategoricalColumns={getCategoricalColumns}
            getNumericColumns={getNumericColumns}
          />
        </TabsContent>

        <TabsContent value="fishbone" className="space-y-6">
          <FishboneTab
            projectId={projectId}
            token={token}
            initialFishbone={initialFishbone}
            initialExistingFishbone={initialExistingFishbone}
          />
        </TabsContent>

        <TabsContent value="hypothesis" className="space-y-6">
          <ManualTestsTab
            testType={testType}
            setTestType={setTestType}
            selectedDataset={selectedDataset}
            datasets={datasets}
            setSelectedDataset={(ds) => { setSelectedDataset(ds); setColumnA(""); setColumnB(""); setTestResult(null); }}
            columnA={columnA}
            setColumnA={setColumnA}
            columnB={columnB}
            setColumnB={setColumnB}
            alpha={alpha}
            setAlpha={setAlpha}
            testResult={testResult}
            setTestResult={setTestResult}
            analyzing={analyzing}
            runHypothesisTest={runHypothesisTest}
            getNumericColumns={getNumericColumns}
            getCategoricalColumns={getCategoricalColumns}
            projectId={projectId}
            token={token}
          />
        </TabsContent>

        <TabsContent value="capability" className="space-y-6">
          <CapabilityTab
            projectId={projectId}
            token={token}
            datasets={datasets}
            selectedDataset={selectedDataset}
            setSelectedDataset={setSelectedDataset}
            getNumericColumns={getNumericColumns}
          />
        </TabsContent>

        <TabsContent value="doe" className="space-y-6">
          <DoeTab projectId={projectId} token={token} />
        </TabsContent>

        <TabsContent value="summary" className="space-y-6">
          <ExecutiveSummaryTab
            projectId={projectId}
            token={token}
            initialSummary={initialExecSummary}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
